import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { BillingSubscription } from "../../../types/subscription";
import { useState } from "react";
import SubscriptionDetails from "./modals/SubscriptionDetails";

export default function SubscriptionsTable({ subscriptions }: { subscriptions: BillingSubscription[] }) {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 14;
    const [openModal, setOpenModal] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState<BillingSubscription | null>(null);

    const totalPages = Math.ceil(subscriptions.length / pageSize);

    const paginatedSubscriptions = subscriptions.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    // Utility: add duration (in days) to date
    const calculateEndDate = (startDate: string, duration: number): Date => {
        const start = new Date(startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + duration);
        return end;
    };

    // Format date as yyyy-mm-dd
    const formatDate = (date: Date) => {
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        };
        return date.toLocaleString("en-US", options);
    };

    return (
        <div className="w-full">
            <div className="w-full bg-card rounded-xl p-4">
                <table className="w-full text-xs">
                    <thead className="border-b border-gray-400 text-gray-200">
                        <tr>
                            <th className="w-[120px] text-center font-light py-2">User</th>
                            <th className="w-[120px] text-center font-light py-2">Service</th>
                            <th className="w-[120px] text-center font-light py-2">Package</th>
                            <th className="w-[120px] text-center font-light py-2">Start Date</th>
                            <th className="w-[120px] text-center font-light py-2">End Date</th>
                            <th className="w-[120px] text-center font-light py-2">Renewal Date</th>
                            <th className="w-[120px] text-center font-light py-2">Amount</th>
                            <th className="w-[120px] text-center font-light py-2">Payer ID</th>
                            <th className="w-[120px] text-center font-light py-2">Status</th>
                            <th className="w-[120px] text-center font-light py-2">Action</th>
                        </tr>
                    </thead>

                    <tbody className="text-white">
                        {paginatedSubscriptions.map((subscription, index) => {
                            const startDate = new Date(subscription.created_at);
                            const renewalDate = new Date(subscription.renewed_at);
                            const endDate = calculateEndDate(subscription.created_at, subscription.duration);
                            const isActive = endDate >= new Date();

                            return (
                                <tr key={index} className={index % 2 === 0 ? "bg-card/50" : "bg-card/30"}>
                                    <td className="w-[120px] text-center py-2">{subscription.username}</td>
                                    <td className="w-[120px] text-center py-2">{subscription.service_name}</td>
                                    <td className="w-[120px] text-center py-2">{subscription.package_name}</td>
                                    <td className="w-[120px] text-center py-2">{formatDate(startDate)}</td>
                                    <td className="w-[120px] text-center py-2">{formatDate(endDate)}</td>
                                    <td className="w-[120px] text-center py-2">{formatDate(renewalDate)}</td>
                                    <td className="w-[120px] text-center py-2">{subscription.price}</td>
                                    <td className="w-[120px] text-center py-2">{subscription.payerid}</td>
                                    <td className={`w-[120px] text-center py-2 ${isActive ? "text-green-500" : "text-red-500"}`}>
                                        {isActive ? "Active" : "Expired"}
                                    </td>
                                    <td className="w-[120px] text-center py-2">
                                        <button className="bg-none border border-white text-white rounded-lg py-1 px-2 hover:scale-105 cursor-pointer"
                                        onClick={() => {
                                            setSelectedSubscription(subscription);
                                            setOpenModal(true);
                                        }}>
                                            View
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-4 text-xs text-white">
                <div className="bg-card flex items-center gap-x-2 text-white text-xs py-1 px-3 rounded-md">
                    <p>Rows per page:</p>
                    <p>{pageSize}</p>
                    <ChevronDown size={16} />
                </div>
                <div className="flex items-center gap-x-2 bg-card rounded-md">
                    <button
                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded disabled:opacity-50"
                    >
                        <ChevronLeft />
                    </button>

                    <span>
                        {currentPage} / {totalPages}
                    </span>

                    <button
                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded disabled:opacity-50"
                    >
                        <ChevronRight />
                    </button>
                </div>
            </div>
            <SubscriptionDetails
                open={openModal}
                onClose={() => setOpenModal(false)}
                subscription={selectedSubscription}
            />
        </div>
    );
}
